import { Avatar } from 'common/constant';
import EditorComponent from 'components/ui/Editor/editor';
import { FileDrop } from 'components/ui/FileDrop/FileDrop';
import FormikValidationError from 'components/ui/FormikErrors';
import TextInput from 'components/ui/TextInput';
import { FormikErrors, FormikProps } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { IBankDetails, IProfile } from './schema';
import { useFetchUserProfileQuery } from 'redux/reducers/administrator';
import { useParams } from 'react-router-dom';
import { BsFileEarmarkPdf } from 'react-icons/bs';
import { toast } from 'react-toastify';
interface IProfileForm {
  formik: FormikProps<IProfile>;
}
const getImage = (image: File) => {
  try {
    return URL.createObjectURL(image);
  } catch (error) {
    return '';
  }
};

const ProfileForm: FC<IProfileForm> = ({ formik }) => {
  const { id } = useParams<{ id: string }>();
  const { data: profileDetail } = useFetchUserProfileQuery(id);

  function getDocumentName(url: string): string {
    const fileNameWithExtension = url?.substring(url?.lastIndexOf('/') + 1);
    const fileNameWithoutExtension = fileNameWithExtension.slice(
      0,
      fileNameWithExtension.lastIndexOf('.')
    );

    // Capitalize the first letter of the file name
    const capitalizedFileName =
      fileNameWithoutExtension.charAt(0).toUpperCase() + fileNameWithoutExtension.slice(1);

    // Check if the file name is longer than 25 characters
    if (capitalizedFileName.length > 25) {
      const truncatedFileName = capitalizedFileName.substring(0, 22) + '...';
      return truncatedFileName; // Return the truncated name with "..."
    } else {
      return capitalizedFileName; // Return the capitalized name
    }
  }

  return (
    <div className="card mt-2 px-4">
      <div className="card-body">
        <div className="text-center">
          <div className="profile-user position-relative d-inline-block mx-auto  mb-4">
            <img
              src={
                formik.values.image
                  ? typeof formik.values.image === 'string'
                    ? formik.values.image
                    : getImage(formik.values.image)
                  : Avatar
              }
              className="rounded-circle avatar-xl img-thumbnail user-profile-image"
              alt="user-profile-image"
            />
            <div className="avatar-xs rounded-circle profile-photo-edit">
              <input
                id="profile-img-file-input"
                type="file"
                className="profile-img-file-input"
                name="image"
                onChange={(e) => {
                  formik.handleChange({
                    target: { name: 'image', value: e.target.files?.[0] ?? null }
                  });
                }}
              />
              <label htmlFor="profile-img-file-input" className="profile-photo-edit avatar-xs">
                <span className="avatar-title rounded-circle bg-blue text-body">
                  <i className="ri-pencil-fill" />
                </span>
              </label>
            </div>
          </div>

          <h5 className="fs-14 mb-1">Profile Image</h5>

          {formik.touched.image && formik.errors.image && (
            <span className="text-danger">{formik.errors.image}</span>
          )}
        </div>
      </div>

      <div className="col-md">
        <EditorComponent
          label="Bio"
          withoutMinHeight
          containerClassName="mt-3"
          editorValue={formik.values.bio}
          onChangeValue={(data) => formik.setFieldValue('bio', data)}
          placeholder="Your bio here...."
        />

        <FormikValidationError name="bio" errors={formik.errors} touched={formik.touched} />
      </div>
      <span className="profile-details-heading mt-4">Personal Details</span>
      <div className="row mb-3">
        <TextInput
          label="First Name"
          name="first_name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.first_name}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
        <TextInput
          label="Middle Name"
          name="middle_name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.middle_name}
          onBlur={formik.handleBlur}
          formik={formik}
          disableRequiredValidation
        />
        <TextInput
          label="Last Name"
          name="last_name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.last_name}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
        <TextInput
          label="Email"
          name="email"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.email}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
        <TextInput
          label="Mobile Number"
          name="mobile_number"
          type="number"
          onChange={formik.handleChange}
          value={formik.values.mobile_number}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
        <TextInput
          label="PAN Number"
          name="pan_number"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.pan_number}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
      </div>
      <div className="mt-4">
        <span className="profile-details-heading">Professional Details</span>
      </div>
      <div className="row mb-3">
        <TextInput
          label="Current Position"
          name="current_position"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.current_position}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
        <TextInput
          label="Associated Organization"
          name="associated_organization"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.associated_organization}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
      </div>
      <div className="mt-4">
        <span className="profile-details-heading">Bank Details</span>
      </div>
      <div className="row mb-3">
        <TextInput
          label="Bank Name"
          name="bank_name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.bank_name}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
        <TextInput
          label="Branch"
          name="bank_branch"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.bank_branch}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
        <TextInput
          label="Account Number"
          name="bank_account_number"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.bank_account_number}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
        <TextInput
          label="Account Name"
          name="bank_account_holder"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.bank_account_holder}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          disableRequiredValidation
        />
      </div>
      <div className="col-md mb-4">
        <EditorComponent
          label="Area of experience"
          containerClassName="mt-3"
          withoutMinHeight
          editorValue={formik.values.experience}
          onChangeValue={(data) => formik.setFieldValue('experience', data)}
          placeholder="Your area of experience here..."
        />
        <FormikValidationError name="experience" errors={formik.errors} touched={formik.touched} />
      </div>
      <div className="col-md mb-3">
        <EditorComponent
          label="Expertise"
          containerClassName="mt-3"
          withoutMinHeight
          editorValue={formik.values.area_of_expertise}
          onChangeValue={(data) => formik.setFieldValue('area_of_expertise', data)}
          placeholder="Your area_of_expertise here..."
        />
        <FormikValidationError
          name="area_of_expertise"
          errors={formik.errors}
          touched={formik.touched}
        />
      </div>
      <span className="profile-details-heading mt-3">Upload CV</span>
      <div className="row">
        {profileDetail?.user_document &&
          profileDetail?.user_document?.length > 0 &&
          profileDetail?.user_document?.map((item: any, idx: number) => {
            return (
              <div
                className="col-3 m-2 d-flex justify-content-center align-items-center"
                style={{
                  height: '230px',
                  width: '225px',
                  border: '1px solid red',
                  background: '#fff',
                  borderRadius: '8px'
                }}
              >
                <a
                  href={item?.document ?? ''}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                  target="_blank"
                >
                  <BsFileEarmarkPdf size={100} color="#5e0404" />
                  <p className="mt-2" style={{ color: '#5e0404' }}>
                    {getDocumentName(item?.document)}
                  </p>
                </a>
              </div>
            );
          })}
        <div className="col-3" style={{ cursor: 'pointer' }}>
          <FileDrop
            files={
              formik.values.documents instanceof File
                ? [formik.values.documents]
                : ([] as unknown as File[])
            }
            setFiles={(data) => {
              const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes

              const file = Array.isArray(data) ? data[0] : data;

              if (file && file.type !== 'application/pdf') {
                toast.warn('Only PDF files are allowed');
                return;
              }

              if (file && file.size > MAX_FILE_SIZE) {
                toast.warn('File size must be less than 2MB');
                return;
              }

              formik.setFieldValue('documents', data);
            }}
            message="Add new file"
            preview={true}
            accept={{ 'application/pdf': ['.pdf'] }}
          />
          <b> {`*** PDF only < 2Mb `}</b>
        </div>
      </div>
      {/* <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: '10px' }}>
        {formik.values.existing_documents &&
          formik?.values?.existing_documents?.length > 0 &&
          formik.values.existing_documents.map((documents, index) => {
            return (
              <img
                src={documents.document}
                className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                alt="user-profile-image"
              />
            );
          })}
      </div> */}
      <FormikValidationError name="documents" errors={formik.errors} touched={formik.touched} />
    </div>
  );
};

export default ProfileForm;
